import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import SecurityIcon from '@mui/icons-material/Security';
import TemplateIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface DealRoomTemplate {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  sections: any[];
  created_at: string;
}

const DealRoomSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Fetch deal room templates
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['dealRoomTemplates'],
    queryFn: async () => {
      const response = await baseClient.get('/deal-room/templates');
      return response.data;
    },
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: async (templateData: any) => {
      const response = await baseClient.post('/deal-room/templates', templateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealRoomTemplates'] });
    },
  });

  const handleCreateSampleTemplate = () => {
    const sampleTemplate = {
      name: "Standard Deal Room",
      description: "Default template for deal rooms with common sections",
      sections: [
        { name: "Overview", type: "text", required: true },
        { name: "Financial Information", type: "documents", required: true },
        { name: "Legal Documents", type: "documents", required: false },
        { name: "Due Diligence", type: "documents", required: false }
      ],
      default_permissions: {
        view: "authenticated",
        download: "authorized",
        comment: "authorized"
      },
      branding: {
        primary_color: "#1976d2",
        logo_url: ""
      },
      is_default: templates?.length === 0,
      is_active: true
    };

    createTemplateMutation.mutate(sampleTemplate);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Deal Room Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure deal room templates, permissions, and collaboration settings for client interactions.
        </Typography>
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Deal room templates are not yet configured. Create your first template to get started.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Templates Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Deal Room Templates
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateSampleTemplate}
                  disabled={createTemplateMutation.isPending}
                >
                  {createTemplateMutation.isPending ? 'Creating...' : 'Create Sample Template'}
                </Button>
              </Box>

              {templates && templates.length > 0 ? (
                <List>
                  {templates.map((template: DealRoomTemplate) => (
                    <React.Fragment key={template.id}>
                      <ListItem>
                        <ListItemIcon>
                          <TemplateIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {template.name}
                              {template.is_default && (
                                <Chip label="Default" size="small" color="primary" />
                              )}
                              {!template.is_active && (
                                <Chip label="Inactive" size="small" color="default" />
                              )}
                            </Box>
                          }
                          secondary={template.description}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<EditIcon />}>
                            Edit
                          </Button>
                          <Button size="small" color="error" startIcon={<DeleteIcon />}>
                            Delete
                          </Button>
                        </Box>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TemplateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Templates Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create your first deal room template to get started with client collaboration.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Panel */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {/* Security Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Security Settings
                  </Typography>
                  <List dense>
                    <ListItem>
                      <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Require Authentication"
                      />
                    </ListItem>
                    <ListItem>
                      <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Enable Download Tracking"
                      />
                    </ListItem>
                    <ListItem>
                      <FormControlLabel
                        control={<Switch />}
                        label="Watermark Documents"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Default Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Default Settings
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Default Template</InputLabel>
                      <Select
                        value={selectedTemplate}
                        label="Default Template"
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                      >
                        {templates?.map((template: DealRoomTemplate) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Session Timeout (hours)"
                      type="number"
                      defaultValue={24}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" variant="outlined" fullWidth>
                    Save Settings
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {createTemplateMutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Sample template created successfully!
        </Alert>
      )}

      {createTemplateMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to create template. Please try again.
        </Alert>
      )}
    </Box>
  );
};

export default DealRoomSettingsPage;
